import i18n from 'i18next';
import { Feature, InjectorService } from '@tencent/workbench';
import BaseTitleBarFeature from '@framework/workbench/common/feature/base-titlebar-feature';
import { UniformFontController } from '@framework/workbench/ui/quick-setting/uniform-font/uniform-font-controller';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { EShowModalSource } from '@src/slide/application/service/theme-color-manager/index.interface';
import { AISidebarFeature, AIShowSceneType } from '@tencent/ai-sdk-interface';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import appConfigInstance from '@src/slide/infrastructure/module/dependent/config/tob/app-config';
import { TitleBarConfig } from '@src/slide/infrastructure/module/dependent/config/config-manager-constants';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWOperationType, TDWModuleType, TDWActionType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';

import aiReadIcon from '@tencent/docs-design-resources/icon/desktop/svg/ai_reading_light_24.svg';
import aiNoteIcon from '@tencent/docs-design-resources/icon/desktop/svg/ai_lengthen_light_24.svg';
import writeIcon from '@tencent/docs-design-resources/icon/desktop/svg/ai_creat_light_24.svg';
import aiWriteIcon from '@tencent/docs-design-resources/icon/desktop/svg/ai_summarize_light_24.svg';
import aiBeautifyIcon from '@tencent/docs-design-resources/icon/desktop/svg/ai_recreat_light_24.svg';
import templateIcon from '@tencent/docs-design-resources/icon/desktop/svg/templates_outline_light_24.svg';
import paletteIcon from '@tencent/docs-design-resources/icon/desktop/svg/palette_light_24.svg';
import ratingIcon from '@tencent/docs-design-resources/icon/desktop/svg/layout_check_outline_light_24.svg';
import fontIcon from '@tencent/docs-design-resources/pc/svg/toolbar_font_setting.svg';
import aiReviewIcon from '@src/img/ai-review.svg';

import type { IPCMainEntryItem } from '@tencent/docs-titlebar-service/dist/feature/TitlebarService/components/ai-assistant';
import { AiReviewService } from '@framework/workbench/ui/ai-review/ai-review-service';
import { AiFloatReportPlace } from '@src/slide/application/service/ai-float-manager/index.interface';

@Feature()
export class aiMenuFeature extends BaseTitleBarFeature {
    private uniformFontController: UniformFontController;

    public constructor(private readonly aiReviewFeature: AiReviewService) {
        super();
        this.uniformFontController = InjectorService.get(UniformFontController);
    }

    public getMenuConfig(): IPCMainEntryItem[] {
        const appServices = AppServiceProvider.getInstance().getAppServices();
        const { aiController } = (appServices as IPcEditorAppServices).editableServiceLayer ?? {};
        if (aiController?.checkIsSupportAIEdit()) {
            return this.getEditMenuConfig();
        }
        if (aiController?.checkIsSupportAI()) {
            return this.getReadMenuConfig();
        }
        return [];
    }

    public shouldHideAIAssistant = () => {
        // cac
        if (appConfigInstance.getConfig(TitleBarConfig.aiAssistant as any) === false) {
            return true;
        }
        const appServices = AppServiceProvider.getInstance().getAppServices();
        return !(appServices as IPcEditorAppServices).editableServiceLayer?.aiController.checkIsSupportAI();
    };

    private getEditMenuConfig(): IPCMainEntryItem[] {
        return [
            {
                name: `AI ${i18n.t('阅读')}`,
                icon: aiReadIcon,
                description: i18n.t('快速总结/深度阅读/思维导图总结'),
                event: () => this.showAiSidebarRead(false),
            },
            {
                name: `AI ${i18n.t('写作')}`,
                icon: writeIcon,
                description: i18n.t('AI生成页/快速生成备注'),
                children: [
                    { name: `AI ${i18n.t('生成页')}`, icon: aiWriteIcon, event: () => this.showAiGeneratePageModal() },
                    { name: `AI ${i18n.t('写备注')}`, icon: aiNoteIcon, event: () => this.showAiSidebarNote() },
                ],
            },
            {
                name: `AI ${i18n.t('美化')}`,
                icon: aiBeautifyIcon,
                description: `${i18n.t('美化')} PPT`,
                children: [
                    { name: i18n.t('文本订正'), icon: aiReviewIcon, event: () => this.showAiFullReview() },
                    { name: i18n.t('排版检查'), icon: ratingIcon, event: () => this.slideRating() },
                    { name: i18n.t('字体切换'), icon: fontIcon, event: () => this.changeFont() },
                    { name: i18n.t('配色切换'), icon: paletteIcon, event: () => this.changeThemeColor() },
                    { name: i18n.t('模板切换'), icon: templateIcon, event: () => this.changeTemplate() },
                ],
            },
        ];
    }

    private getReadMenuConfig(): IPCMainEntryItem[] {
        return [
            {
                name: `AI ${i18n.t('阅读')}`,
                icon: aiReadIcon,
                description: i18n.t('快速总结/深度阅读/思维导图总结'),
                event: () => this.showAiSidebarRead(true),
            },
        ];
    }

    /**
     * 以 AI 阅读的方式打开 AI 侧边栏
     */
    private showAiSidebarRead(isReadOnly: boolean) {
        tdw({
            opername: TDWOperationType.DOC_PPT_AI,
            module: TDWModuleType.READ,
            action: TDWActionType.CLICK,
            ver5: TDWModuleType.PC,
        });
        const aiService = this.getAiService()?.promise();
        const featureList = isReadOnly ? [
            AISidebarFeature.SUMMARY,
            AISidebarFeature.DEEPREAD,
            AISidebarFeature.MINDMAP,
        ] : [
            AISidebarFeature.SUMMARY,
            AISidebarFeature.DEEPREAD,
            AISidebarFeature.MINDMAP,
            AISidebarFeature.FILEQA,
        ];

        aiService?.then(aiService =>
            aiService.showSideBar({
                featureList,
            }));
    }

    /**
     * 以 AI 写备注的方式打开 AI 侧边栏
     */
    private showAiSidebarNote() {
        tdw({
            opername: TDWOperationType.DOC_PPT_AI,
            module: TDWModuleType.WRITE_NOTE,
            action: TDWActionType.CLICK,
            ver5: TDWModuleType.PC,
        });
        const aiService = this.getAiService()?.promise();
        aiService?.then(aiService =>
            aiService.showSideBar({
                featureList: [AISidebarFeature.FILEQA],
                activeFeature: {
                    id: AISidebarFeature.FILEQA,
                    commandId: 'e2szs8u8q1',
                },
                scene: {
                    type: AIShowSceneType.SLIDE_AI_GENERATE_NOTES,
                },
            }));
    }

    /**
     * 调起 AI 生成页悬浮窗口
     */
    private showAiGeneratePageModal() {
        tdw({
            opername: TDWOperationType.DOC_PPT_AI,
            module: TDWModuleType.PAGE_CREATE,
            action: TDWActionType.CLICK,
            ver5: TDWModuleType.PC,
            ver6: AiFloatReportPlace.aiTab,
        });
        const appServices = AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices;
        const aiFloatManager = appServices?.editableServiceLayer?.aiFloatManager;

        aiFloatManager?.triggerGeneratePage({});
    }

    private showAiWriteMenu() {
        tdw({
            opername: TDWOperationType.DOC_PPT_AI,
            module: TDWModuleType.FULL_REVIEW,
            action: TDWActionType.AI_VIEW,
            ver5: TDWModuleType.PC,
        });
    }

    /**
     * AI审阅
     * @private
     */
    private async showAiFullReview() {
        // 排版检查和 AI 区域有重叠，手动关闭 AI 助手
        this.getAppServices().editableServiceLayer?.aiService?.promise().then((aiService) => {
            aiService.hideSideBar();
        });

        tdw({
            opername: TDWOperationType.DOC_PPT_AI,
            module: TDWModuleType.FULL_REVIEW,
            action: TDWActionType.AI_CLK,
            ver5: TDWModuleType.PC,
        });

        this.aiReviewFeature.showDrawer();
    }

    private slideRating() {
        tdw({
            opername: TDWOperationType.DOC_PPT_AI,
            module: TDWModuleType.LAYOUT_CHECK,
            action: TDWActionType.CLICK,
            ver5: TDWModuleType.PC,
            ver: TDWModuleType.AI_TITLEBAR,
        });
        this.hideAiSidebar();
        this.getAppServices().editableServiceLayer?.ratingService?.promise().then((ratingService) => {
            ratingService.showDrawer();
        });
    }

    private changeFont() {
        tdw({
            opername: TDWOperationType.DOC_PPT_AI,
            module: TDWModuleType.FONT_SWITCH,
            action: TDWActionType.CLICK,
            ver5: TDWModuleType.PC,
        });
        this.uniformFontController.onClick({});
    }

    private changeThemeColor() {
        tdw({
            opername: TDWOperationType.DOC_PPT_AI,
            module: TDWModuleType.COLOR_SWITCH,
            action: TDWActionType.CLICK,
            ver5: TDWModuleType.PC,
        });
        this.getAppServices().editableServiceLayer?.themeColorManager?.showThemeModal({
            source: EShowModalSource.ai,
        });
    }

    private changeTemplate() {
        tdw({
            opername: TDWOperationType.DOC_PPT_AI,
            module: TDWModuleType.TEMPLATE_SWITCH,
            action: TDWActionType.CLICK,
            ver5: TDWModuleType.PC,
        });
        this.getAppServices().editableServiceLayer?.changeTemplateService?.promise().then((module) => {
            module.showTemplateBeautifyDialog(undefined);
        });
    }

    private hideAiSidebar() {
        this.getAiService()
            ?.promise()
            .then(s => s.hideSideBar());
    }
}
